import React, { useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, ListItemText, TextField, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { isEmpty, sortBy } from 'lodash';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';

import { dspState } from '../../../atoms/DspState';
import { prefixesFilterState } from '../../../atoms/PrefixesFilterState';
import { prefixesManagement } from '../../../atoms/PrefixesManagementState';
import { warehouseState } from '../../../atoms/WarehouseState';
import { AxlButton } from '../../../components/AxlButton';
import { addPrefix, editPrefix, getPrefixDetail, getRouteTypes, getZoneIds, listWarehouseRegion } from '../../../repositories/HttpRepository';
import InputField from './Elements/inputField';
import Select from './Elements/select';
import * as Styles from './styles';

const AddPrefix = () => {
  const [errorAdd, setErrorAdd] = useState('');
  const [regionList, setRegionList] = useState([]);
  const [enableAddBtn, setEnableAddBtn] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prefix, setPrefix] = useState();
  const [regionCode, setRegionCode] = useState();
  const [warehouseId, setWarehouseId] = useState();
  const [dspId, setDspId] = useState();
  const [capacity, setCapacity] = useState();
  const [zoneId, setZoneId] = useState('');
  const [maxBoxes, setMaxBoxes] = useState();
  const [sprinklable, setSprinkable] = useState(true);
  const [zoneIdsData, setZoneIdsData] = useState([]);
  const [routeType, setRouteType] = useState(null);
  const [optRouteTypes, setOptRouteTypes] = useState([]);

  // const [currentPrefix, setCurrentPrefix] = useRecoilState(detailPrefix);
  const [warehouses, setWarehouses] = useRecoilState(warehouseState);
  const [dspList, setDspList] = useRecoilState(dspState);
  const [prefixes, setPrefixes] = useRecoilState(prefixesManagement);
  const [filterStates, setFilterState] = useRecoilState(prefixesFilterState);

  const { id } = useParams();

  const { classes } = Styles.useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  function processFilterData() {
    const params = new URLSearchParams();
    const urlParams = new URLSearchParams(location.search);
    const queryParams = Object.fromEntries(urlParams);
    const mergeParams = {
      ...queryParams,
      ...filterStates.selected,
      ...filterStates.sort,
    };

    for (const [key, value] of Object.entries(mergeParams)) {
      if (!value) {
        continue;
      }

      if (Array.isArray(value)) {
        value.forEach((tmp) => {
          params.append(key, tmp);
        });
      } else {
        params.append(key, value);
      }
    }
    return params;
  }

  const handleCancel = (e) => {
    const urlParams = new URLSearchParams(processFilterData());
    urlParams.set('page', urlParams.get('page') <= 1 ? 1 : +urlParams.get('page') + 1);
    navigate(
      {
        pathname: '/prefixes-management',
        search: `?${urlParams.toString()}`,
      },
      { replace: true },
    );
  };

  const listRegions = (params, callback) => {
    listWarehouseRegion(params).then((resp) => {
      if (resp.ok) {
        const items = sortBy(resp.data.items);
        let regions = [];
        items.forEach((item) => {
          regions.push({ label: item, value: item });
        });
        callback(regions);
        return;
      }
    });
  };

  const getPayload = () => ({
    prefix: prefix?.trim(),
    region_code: regionCode,
    warehouse_id: warehouseId,
    dsp_id: dspId,
    capacity,
    zone_id: zoneId,
    max_box: maxBoxes,
    sprinkle: sprinklable,
    route_type: routeType,
  });

  const onEditPrefixHandle = async () => {
    setIsAdding(true);
    const resp = await editPrefix(id, getPayload());
    setIsAdding(false);
    if (!resp.ok) {
      toast.error(resp.data.message || 'Something went wrong!');
      return;
    }

    setFilterState({
      ...filterStates,
      canRefresh: !filterStates.canRefresh || false,
    });
    toast.success('Edit prefix successfully');
    handleCancel();
  };

  const onAddPrefixHandle = async () => {
    setIsAdding(true);
    const resp = await addPrefix(getPayload());
    setIsAdding(false);
    if (!resp.ok) {
      toast.error(resp.data.message || 'Something went wrong!');
      return;
    }

    setFilterState({
      ...filterStates,
      sort: { order_by: 'id', is_desc: true },
      selected: {
        ...filterStates.selected,
        'warehouse_ids[]': null,
        'regions[]': null,
        page: 0,
      },
    });
    toast.success('Add prefix successfully');
    handleCancel();
  };

  const warehouseOptions = useMemo(() => {
    return regionCode
      ? warehouses?.items
          ?.map((warehouse) => {
            const value = warehouse.id;
            const addressArr = [warehouse.address.street, warehouse.address.city, warehouse.address.state, warehouse.address.zipcode];
            const label = warehouse.alias ? `${warehouse.alias} @ ${addressArr.join(', ')}` : `${addressArr.join(', ')}`;
            const alias = warehouse.alias;
            const address = addressArr.join(', ');
            return warehouse.region === regionCode
              ? {
                  label,
                  value,
                  alias,
                  address,
                }
              : undefined;
          })
          .filter((item) => item !== undefined)
      : [];
  }, [warehouses, regionCode]);

  const dspOptions = useMemo(
    () =>
      dspList?.items?.map((dsp) => ({
        label: dsp.code || dsp.company,
        value: dsp.id,
        type: dsp.type,
      })),
    [dspList],
  );

  const zoneIdsOptions = useMemo(
    () =>
      zoneIdsData?.map((item) => ({
        label: item.zone_name,
        value: item.zone_id,
      })),
    [zoneIdsData],
  );

  const handleChangeRegionCode = (e) => {
    const newValue = e.target.value;
    setRegionCode(newValue);
    if (newValue?.length > 0 && errors.includes('region_code')) {
      setErrors([...errors.filter((error) => error !== 'region_code')]);
    }

    if (newValue?.length === 0 && !errors.includes('region_code')) {
      setErrors([...errors, 'region_code']);
    }

    if (newValue?.length > 0) {
      setZoneId(undefined);
      setWarehouseId(undefined);
    }
  };

  const submitForm = (e) => {
    e.preventDefault();

    const tempErrors = [];

    if (!prefix || prefix.length === 0) {
      tempErrors.push('prefix');
    }

    if (!regionCode || regionCode.length === 0) {
      tempErrors.push('region_code');
    }

    if (!warehouseId && !dspId) {
      tempErrors.push('warehouse_id');
      tempErrors.push('dsp_id');
    }

    if (maxBoxes && maxBoxes !== '' && (isNaN(maxBoxes) || maxBoxes < 0)) {
      tempErrors.push('max_box');
    }

    if (capacity && capacity !== '' && (isNaN(capacity) || capacity < 0)) {
      tempErrors.push('capacity');
    }

    if (!routeType) {
      tempErrors.push('route_type');
    }

    setErrors(tempErrors);

    if (tempErrors.length === 0) {
      id ? onEditPrefixHandle() : onAddPrefixHandle();
    }
  };

  useEffect(() => {
    if (prefix?.length > 0 && errors.includes('prefix')) {
      setErrors([...errors.filter((error) => error !== 'prefix')]);
    }

    if (prefix?.length === 0 && !errors.includes('prefix')) {
      setErrors([...errors, 'prefix']);
    }
  }, [prefix]);

  useEffect(() => {
    if (!warehouseId && !dspId && !errors.includes('warehouse_id') && !errors.includes('dsp_id') && regionCode) {
      setErrors([...errors, 'dsp_id', 'warehouse_id']);
    }

    if (warehouseId || dspId) {
      setErrors([...errors.filter((error) => error !== 'dsp_id' && error !== 'warehouse_id')]);
    }
  }, [dspId, warehouseId]);

  useEffect(() => {
    if (maxBoxes && maxBoxes !== '' && (isNaN(maxBoxes) || maxBoxes < 0)) {
      setErrors([...errors, 'max_box']);
    } else {
      setErrors([...errors.filter((error) => error !== 'max_box')]);
    }
  }, [maxBoxes]);

  useEffect(() => {
    if (capacity && capacity !== '' && (isNaN(capacity) || capacity < 0)) {
      setErrors([...errors, 'capacity']);
    } else {
      setErrors([...errors.filter((error) => error !== 'capacity')]);
    }
  }, [capacity]);

  useEffect(() => {
    if (regionCode?.length > 0) {
      getZoneIds({ region: regionCode })
        .then((resp) => {
          if (resp.ok) {
            setZoneIdsData(resp.data);
          }
        })
        .catch((err) => toast.error(err));
    }
  }, [regionCode]);

  // Get init regions
  useEffect(() => {
    listRegions({}, (regions) => {
      setRegionList(regions);
    });
    getRouteTypes().then((resp) => {
      if (!resp.ok) {
        setOptRouteTypes([]);
        return;
      }

      const dataRouteTypes =
        resp?.data?.route_types?.map((item) => ({
          label: item,
          value: item,
        })) || [];
      setOptRouteTypes(dataRouteTypes);
    });
  }, []);

  const updateFields = (data) => {
    setPrefix(data?.prefix);
    setRegionCode(data?.region_code);
    setWarehouseId(data?.warehouse_id);
    setDspId(data?.dsp_id);
    setCapacity(data?.capacity);
    setZoneId(data?.zone_id);
    setMaxBoxes(data?.max_box);
    setSprinkable(data?.sprinkle);
    setRouteType(data?.route_type);
  };

  useEffect(() => {
    if (!isEmpty(prefixes?.data)) {
      const currentPrefix = prefixes?.data?.find((item) => item.id.toString() === id);
      if (currentPrefix) {
        updateFields(currentPrefix);
      } else {
        if (!id) return;

        getPrefixDetail(id).then((resp) => {
          if (!resp.ok) {
            toast.error(resp.data.message || 'Something went wrong!');
            updateFields({});
            return;
          }

          updateFields(resp.data);
        });
      }
    }
  }, [prefixes?.data]);

  const handleRouteTypeChange = (e, values) => {
    if (isEmpty(values)) {
      setErrors([...errors, 'route_type']);
    } else {
      setErrors([...errors.filter((error) => error !== 'route_type')]);
    }
    setRouteType(values?.value);
  };

  return (
    <Dialog open={true} onClose={handleCancel} aria-labelledby="form-dialog-title" maxWidth="md">
      <DialogTitle id="form-dialog-title" className={classes.Dialog}>
        <Typography variant="h4">
          {id ? 'Edit Prefix' : 'Add New Prefix'}
          <CloseIcon className={classes.closeBtn} onClick={handleCancel} />
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.formAdd}>
        <Typography color="error" variant="caption">
          {errorAdd}
        </Typography>

        {(id && isLoading) || !warehouseOptions || !dspOptions === 0 || !zoneIdsOptions ? (
          <div
            style={{
              width: '100%',
              height: 320,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={28} />
          </div>
        ) : (
          <form className={classes.root} noValidate autoComplete="off">
            <Grid container spacing={3}>
              <Grid size={6}>
                <InputField
                  label={
                    <>
                      <span>Prefix*</span>
                      <span className={classes.required}>(required)</span>
                    </>
                  }
                  id="prefix"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                  error={errors.includes('prefix')}
                  hint={errors.includes('prefix') && `Prefix is required`}
                  inputProps={{ maxLength: 6 }}
                />
              </Grid>

              <Grid size={6}>
                <Select
                  label={
                    <>
                      <span>Region Code*</span>
                      <span className={classes.required}>(required)</span>
                    </>
                  }
                  id="region_code"
                  options={regionList}
                  onChange={handleChangeRegionCode}
                  value={regionCode}
                  error={errors.includes('region_code')}
                  hint={errors.includes('region_code') && `Region code is required`}
                />
              </Grid>

              <Grid size={6}>
                <Autocomplete
                  value={optRouteTypes?.find((item) => item.value === routeType) || null}
                  options={optRouteTypes}
                  renderInput={(params) => (
                    <>
                      <div>
                        <span>Route Type*</span>
                        <span className={classes.required}>(required)</span>
                      </div>
                      <TextField {...params} variant="outlined" size="small" fullWidth error={errors.includes('route_type')} helperText={errors.includes('route_type') && 'Route Type is required'} />
                      <p className={classes.description}>Route type of this prefix</p>
                    </>
                  )}
                  getOptionLabel={(option) => `${option.label}`}
                  onChange={handleRouteTypeChange}
                />
              </Grid>

              <Grid size={6}>
                <Autocomplete
                  disabled={!warehouses?.items}
                  value={warehouseOptions?.find((warehouse) => warehouse.value === warehouseId) || null}
                  options={warehouseOptions}
                  renderInput={(params) => {
                    return (
                      <>
                        <span>Warehouse</span>
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={errors.includes('warehouse_id')}
                          helperText={errors.includes('warehouse_id') && 'Warehouse or DSP is required'}
                        />
                        <p className={classes.description}>Routes with this prefix will be set pickup location to selected warehouse</p>
                      </>
                    );
                  }}
                  getOptionLabel={(option) => `[${option.value || '-'}] ${option.label}`}
                  renderOption={(props, option) => (
                    <ListItemText {...props}>
                      [{option.value}] -{' '}
                      {option.alias && (
                        <>
                          <b>{option.alias}</b> @
                        </>
                      )}{' '}
                      {option.address}
                    </ListItemText>
                  )}
                  onChange={(e, values) => setWarehouseId(values?.value)}
                />
              </Grid>
              <Grid size={6}>
                <Autocomplete
                  disabled={!dspList?.items}
                  value={dspOptions?.find((dsp) => dsp.value === dspId)}
                  options={dspOptions}
                  renderInput={(params) => (
                    <>
                      <span>DSP</span>
                      <TextField {...params} variant="outlined" size="small" fullWidth error={errors.includes('dsp_id')} helperText={errors.includes('dsp_id') && 'Warehouse or DSP is required'} />
                      <p className={classes.description}>Routes with this prefix will be auto assigned to selected DSP</p>
                    </>
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <span>
                        [{option.value || '-'}] {option.label}
                        {option.type && <span style={{ fontSize: '0.85em', color: '#888', marginLeft: '4px' }}>({option.type})</span>}
                      </span>
                    </li>
                  )}
                  onChange={(e, values) => setDspId(values?.value)}
                />
              </Grid>
              <Grid size={6}>
                <InputField
                  label="Capacity"
                  type="number"
                  id="capacity"
                  onChange={(e) => setCapacity(e.target.value)}
                  value={capacity}
                  inputProps={{ min: 0 }}
                  error={errors.includes('capacity')}
                  hint={errors.includes('capacity') && 'Invalid Capacity'}
                />
                <p className={classes.description}>Max capacity allowed for this route prefix (using for sprinkling)</p>
              </Grid>
              <Grid size={6}>
                <Autocomplete
                  disabled={zoneIdsOptions.length === 0}
                  value={zoneIdsOptions?.find((item) => item.value === zoneId) || null}
                  options={zoneIdsOptions}
                  renderInput={(params) => (
                    <>
                      <span>Booking Zone</span>
                      <TextField {...params} variant="outlined" size="small" fullWidth />
                      <p className={classes.description}>Routes with this prefix will be added to selected booking zone in ticket booking</p>
                    </>
                  )}
                  getOptionLabel={(option) => `${option.label}`}
                  onChange={(e, values) => setZoneId(values?.value)}
                />
              </Grid>
              <Grid size={6}>
                <InputField
                  label="Max Boxes"
                  type="number"
                  id="max_box"
                  onChange={(e) => setMaxBoxes(e.target.value)}
                  value={maxBoxes}
                  inputProps={{ min: 0 }}
                  error={errors.includes('max_box')}
                  hint={errors.includes('max_box') && 'Invalid Max Boxes'}
                />
                <p className={classes.description}>Max boxes allowed for this route prefix (using for sprinkling)</p>
              </Grid>

              <Grid
                style={{
                  display: 'flex',
                  justifyContent: 'left',
                  alignItems: 'center',
                }}
                size={6}
              >
                <div>
                  <FormControlLabel
                    label="Sprinklable"
                    control={<Checkbox checked={sprinklable} color="primary" size="small" onChange={(e) => setSprinkable(e.target.checked)} column="3" />}
                    style={{ height: 20, marginLeft: '-8px' }}
                  />
                  <p className={classes.description}>Routes with this prefix can be used for sprinkle</p>
                </div>
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogAction}>
        <AxlButton onClick={handleCancel} isCancel variant="contained" className={classes.cancelBtn}>
          CANCEL
        </AxlButton>
        <Button className={enableAddBtn === true ? 'active' : ''} onClick={submitForm} disabled={isLoading || isAdding} style={{ color: 'white', padding: '8px 30px', minWidth: 120 }}>
          {isAdding && <CircularProgress size={14} style={{ marginRight: 10, color: 'white' }} />} {id ? 'SAVE' : 'ADD'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPrefix;
